import { useField } from 'formik';
import React from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Radio,
  Typography,
  Skeleton
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MoneyCents, Tooltip } from '@uplift/ui-v2';

import { useAppointmentByIdQuery, useMedicalNoteDetailsByIdQuery, useUserQuery, useUserBillingQuery } from '@uplift/queries';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { AppointmentType, Payment } from '@uplift/api';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { ProviderMedicalRole } from '@uplift/types';
import { mobileBreakpoint, useIsMobile } from '@uplift/hooks';


const useStyles = makeStyles()({
  disabledTooltip: {
    marginTop: 0,
  },
  singleSessionListItem: {
    paddingTop: '4px',
    paddingBottom: '4px',
    cursor: 'pointer',
  },
});


const SessionPaymentLabel: React.VFC<{ id: string }> = ({ id }) => {
  const timezone = useTimezone();
  const { data: appointment } = useAppointmentByIdQuery(id, { suspense: true });

  const date = React.useMemo(() => CalendarDateService.date(appointment.fromDatetime).tz(timezone).format(DateFormats.DEFAULT_DATE), [ appointment, timezone ]);
  const label = React.useMemo(() => {
    if (appointment.chargeFee) {
      return `Late Fee on ${date}`;
    }
    if (appointment.type === AppointmentType.therapy) {
      return `Therapy on ${date}`;
    }
    if (appointment.type === AppointmentType.psychiatry) {
      return `Psychiatry on ${date}`;
    }
  }, [ appointment, date ]);

  return (
    <ListItemText primary={label} />
  );
};

const MedicalNotePaymentLabel: React.VFC<{ id: string }> = ({ id }) => {
  const { data: noteDetails } = useMedicalNoteDetailsByIdQuery(id, { suspense: true });
  const { data: provider } = useUserQuery(noteDetails.providerId, { suspense: true });

  const date = React.useMemo(() => {
    return CalendarDateService.date(noteDetails.serviceDate).format(DateFormats.DEFAULT_DATE);
  }, [ noteDetails ]);
  const label = React.useMemo(() => {
    if (provider.medicalRole === ProviderMedicalRole.therapist) {
      return `Therapy on ${date}`;
    }

    if (provider.medicalRole === ProviderMedicalRole.psychiatrist) {
      return `Psychiatry on ${date}`;
    }

    return `Medical Note on ${date}`;
  }, [ date, provider ]);

  return (
    <ListItemText primary={label} />
  );
};


export const PaymentsPicker: React.VFC<{ name: string }> = ({ name }) => {
  const isMobile = useIsMobile(mobileBreakpoint);
  const me = useCurrentuser();
  const { classes } = useStyles();
  const { data } = useUserBillingQuery(me.id);
  const [ field, _meta, helpers ] = useField<null | Payment[]>(name);

  const selected = field.value === null ? 'wholeBalance' : 'selectedSessions';
  const selectedPayments = field.value ?? [];

  return (
    <Box>
      <Box px={{ xs: 2, sm: 0 }}>
        <Typography variant="h4">Select Amount</Typography>
      </Box>
      <Box mt={1}>
        <Paper square variant={isMobile ? 'outlined' : 'elevation'}>
          <List style={{ padding: 0 }}>
            <ListItem
              style={{ cursor: 'pointer' }}
              divider
              onClick={() => {
                helpers.setValue(null);
              }}
            >
              <ListItemIcon>
                <Radio checked={selected === 'wholeBalance'} />
              </ListItemIcon>
              <ListItemText primary="Current balance" />
              <Typography style={{ fontWeight: 500 }}>
                <MoneyCents value={data.outstandingBalanceCents} />
              </Typography>
            </ListItem>
            <ListItem
              onClick={() => {
                helpers.setValue([ data.outstandingPayments[0] ]);
              }}
              style={{ flexDirection: 'column', cursor: 'pointer' }}
            >
              <Box display="flex" flex="1" alignItems="center" width="100%">
                <ListItemIcon>
                  <Radio checked={selected === 'selectedSessions'} />
                </ListItemIcon>
                <ListItemText primary="Select sessions to pay" />
              </Box>
              <Collapse in={selected === 'selectedSessions'} style={{ width: '100%' }} mountOnEnter>
                <Box width="100%" pl={{ xs: 0, sm: 2 }} style={{ cursor: 'auto' }}>
                  <List
                    style={{ padding: 0 }}
                    onMouseDown={e => {
                      // prevent parent ripple effect
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={e => {
                      // prevent parent click
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  >
                    {data?.outstandingPayments?.map((p, _index, arr) => {
                      const disabled = arr.length === 1;
                      const selected = !!selectedPayments.find(s => s.resourceId === p.resourceId);

                      return (
                        <ListItem
                          key={p.resourceId}
                          className={classes.singleSessionListItem}
                          onClick={() => {
                            if (disabled) {
                              return;
                            }

                            if (selected) {
                              helpers.setValue(selectedPayments.filter(s => s.resourceId !== p.resourceId));
                            } else {
                              helpers.setValue([ ...selectedPayments, p ]);
                            }
                          }}
                        >
                          <ListItemIcon>
                            <Tooltip
                              title={disabled ? 'A session must be selected to make a payment' : ''}
                              placement="bottom"
                              arrow={false}
                              classes={{ tooltip: classes.disabledTooltip }}
                            >
                              <span>
                                <Checkbox
                                  disabled={disabled}
                                  checked={!!selected}
                                  color="secondary"
                                />
                              </span>
                            </Tooltip>
                          </ListItemIcon>
                          <React.Suspense
                            fallback={(
                              <ListItemText
                                primary={<Skeleton variant="text" width="175px" />}
                              />
                            )}
                          >
                            {p.resourceType === 'appointment' && <SessionPaymentLabel id={p.resourceId} />}
                            {p.resourceType === 'medical_note' && <MedicalNotePaymentLabel id={p.resourceId} />}
                          </React.Suspense>
                          <Typography>
                            <MoneyCents value={p.amountCents} />
                          </Typography>
                        </ListItem>
                      );
                    })}
                  </List>
                  <Box my={1} display="flex" width="100%" justifyContent="flex-end" px={2}>
                    <Typography style={{ fontWeight: 500 }}>
                  Selected total: <MoneyCents value={selectedPayments.reduce((prev, curr) => prev + curr.amountCents, 0)} />
                    </Typography>
                  </Box>
                </Box>
              </Collapse>
            </ListItem>
          </List>
        </Paper>
      </Box>
    </Box>
  );
};
